import React, { FC } from 'react';
import { Card, Image, Popover, Spin } from 'antd';
import { CertificateEmptyIcon, CommentIcon } from 'components/icons';
import { useImageBlobSrc } from 'hooks';
import { EpCertificate } from 'types/graphql-types';
import styles from './styles.less';

type CertificateProps = EpCertificate;

const NO_DATES_SYMBOL = '...';

const formatDate = (date?: string | null): string => {
  if (!date) {
    return '';
  }

  return new Date(date).toLocaleDateString('eng', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const getDates = (
  validFrom?: string | null,
  validTo?: string | null,
): string => {
  const hasDates = validFrom || validTo;

  if (!hasDates) {
    return '';
  }

  const validFromFormatted = formatDate(validFrom) || NO_DATES_SYMBOL;
  const validToFormatted = formatDate(validTo) || NO_DATES_SYMBOL;

  return `${validFromFormatted} - ${validToFormatted}`;
};

export const Certificate: FC<CertificateProps> = ({
  certificate,
  image,
  vendor,
  validFrom,
  validTo,
  comment,
}) => {
  const imageSource = image || '';
  const { src, isImageLoading } = useImageBlobSrc(imageSource);

  const imageComponent =
    src || imageSource ? (
      <Image
        className={styles.certificate}
        src={src || imageSource}
        alt={certificate || 'Certificate'}
        preview={false}
      />
    ) : (
      <div className={styles.certificate}>
        <CertificateEmptyIcon />
      </div>
    );

  const dates = getDates(validFrom as string, validTo as string);

  return (
    <Card
      className={styles.card}
      bordered={false}
      cover={
        isImageLoading ? (
          <div className={styles.certificate}>
            <Spin />
          </div>
        ) : (
          imageComponent
        )
      }
    >
      <div className={styles['vendor']}>
        {vendor && `${vendor}. `}
        {certificate}
      </div>
      <div className={styles['date']}>
        {dates}
        {comment && (
          <Popover placement="right" title="Comment" content={comment}>
            <CommentIcon />
          </Popover>
        )}
      </div>
    </Card>
  );
};
